import React, { useState } from 'react';
import { gql } from '@soundxyz/gql-string';
import { CopyLinkMenuItem } from '@components/basics/Dropdowns/MenuItems/CopyLinkMenuItem';
import { EditFeaturedSoundMenuItem } from '@components/basics/Dropdowns/MenuItems/EditFeaturedSoundMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { useAuth } from '@context/auth/base';
import { useToast } from '@context/toast';
import { RefetchOperationsOnComplete } from '@graphql/helpers/effects';
import type { FragmentType } from '@graphql/reactQuery';
import { GetUserProfileNewDocument } from '@graphql/reactQuery';
import {
  CollectorActionBarFragmentDoc,
  getFragment,
  UpsertUserBannerImageDocument,
} from '@graphql/reactQuery';
import { FollowEdit } from './FollowEdit';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectorActionBar on User {
    id
    webappUri
    collectedReleasesCount
    bannerImage {
      id
      url
    }
  }

  mutation upsertUserBannerImage($bannerImage: UploadedMedia!) {
    upsertUserBannerImage(bannerImage: $bannerImage) {
      id
      bannerImage {
        id
        key
        url
      }
    }
  }
`);

RefetchOperationsOnComplete({
  trigger: [UpsertUserBannerImageDocument],
  refetch: [GetUserProfileNewDocument],
});

export const CollectorActionBar = ({
  actionBarData,
}: {
  actionBarData: FragmentType<CollectorActionBarFragmentDoc>;
}) => {
  const { authUser } = useAuth();
  const { id, webappUri, collectedReleasesCount } = getFragment(
    CollectorActionBarFragmentDoc,
    actionBarData,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const { openToast } = useToast();

  const isOwner = authUser?.id === id;

  return (
    <div className="flex items-center gap-2">
      <ThreeDotsDropdown isDropdownOpen={isDropdownOpen} setIsDropdownOpen={setIsDropdownOpen}>
        <CopyLinkMenuItem
          onClick={() => {
            navigator.clipboard.writeText(webappUri);
            openToast({
              text: 'Link copied to clipboard',
              variant: 'primary',
            });
          }}
        />
        {isOwner && collectedReleasesCount > 0 && (
          <EditFeaturedSoundMenuItem label="Edit Featured Sound" webappUri={authUser.webappUri} />
        )}
      </ThreeDotsDropdown>
      <FollowEdit userId={id} owner={isOwner} />
    </div>
  );
};
